// crewbit-admin/src/components/money/BulkActionToolbar.tsx
//
// §Money.HardeningBulkPass2 Phase 3 — bulk-action toolbar shown above the
// invoices table when ≥1 row is selected. 4 action buttons:
//   - Delete drafts (visible when ALL selected status='draft')
//   - Cancel (visible when ALL selected status IN ('sent','viewed','overdue'))
//   - Send reminder (same eligibility as cancel)
//   - Mark paid (same eligibility as cancel)
//
// Per spec §4.4 eligibility predicates: ALL selected rows must satisfy the
// per-action status whitelist. Mixed selections disable the action button
// with a tooltip explaining why.
//
// Composer (Invoices.tsx) owns selection state; toolbar receives it as a prop.

import { Trash2, XCircle, Mail, CheckCircle } from 'lucide-react';
import { palette, radii, spacing, transition, typography } from '../../styles/tokens';
import type { InvoiceRow } from '../../types';
import type { BulkActionKind } from './BulkActionConfirmModal';

export interface BulkActionToolbarProps {
  selectedInvoices: InvoiceRow[];
  onClearSelection: () => void;
  onActionClick: (action: BulkActionKind) => void;
}

const ELIGIBLE_STATUSES_BY_ACTION: Record<BulkActionKind, string[]> = {
  delete_drafts: ['draft'],
  cancel: ['sent', 'viewed', 'overdue'],
  send_reminder: ['sent', 'viewed', 'overdue'],
  mark_paid: ['sent', 'viewed', 'overdue'],
};

function isEligibleForAction(invoices: InvoiceRow[], action: BulkActionKind): boolean {
  if (invoices.length === 0) return false;
  const eligible = ELIGIBLE_STATUSES_BY_ACTION[action];
  return invoices.every((i) => eligible.includes(i.status));
}

function ineligibleReason(invoices: InvoiceRow[], action: BulkActionKind): string {
  const eligible = ELIGIBLE_STATUSES_BY_ACTION[action];
  const ineligible = invoices.filter((i) => !eligible.includes(i.status));
  if (ineligible.length === 0) return '';
  const eligibleLabel = eligible.length === 1 ? eligible[0] : `${eligible.slice(0, -1).join(', ')} or ${eligible[eligible.length - 1]}`;
  return `Select only ${eligibleLabel} invoices (${ineligible.length} ineligible).`;
}

export function BulkActionToolbar({ selectedInvoices, onClearSelection, onActionClick }: BulkActionToolbarProps) {
  const n = selectedInvoices.length;
  if (n === 0) return null;

  return (
    <div style={toolbarStyle} role="toolbar" aria-label="Bulk invoice actions">
      <div style={selectionLabelStyle}>
        <strong>{n}</strong> selected
        <button
          type="button"
          onClick={onClearSelection}
          style={clearButtonStyle}
          aria-label="Clear selection"
        >
          Clear
        </button>
      </div>

      <div style={actionsRowStyle}>
        <ActionButton
          label="Delete drafts"
          icon={<Trash2 size={14} />}
          tone="danger"
          eligible={isEligibleForAction(selectedInvoices, 'delete_drafts')}
          tooltip={ineligibleReason(selectedInvoices, 'delete_drafts')}
          onClick={() => onActionClick('delete_drafts')}
        />
        <ActionButton
          label="Cancel"
          icon={<XCircle size={14} />}
          tone="danger"
          eligible={isEligibleForAction(selectedInvoices, 'cancel')}
          tooltip={ineligibleReason(selectedInvoices, 'cancel')}
          onClick={() => onActionClick('cancel')}
        />
        <ActionButton
          label="Send reminder"
          icon={<Mail size={14} />}
          tone="default"
          eligible={isEligibleForAction(selectedInvoices, 'send_reminder')}
          tooltip={ineligibleReason(selectedInvoices, 'send_reminder')}
          onClick={() => onActionClick('send_reminder')}
        />
        <ActionButton
          label="Mark paid"
          icon={<CheckCircle size={14} />}
          tone="default"
          eligible={isEligibleForAction(selectedInvoices, 'mark_paid')}
          tooltip={ineligibleReason(selectedInvoices, 'mark_paid')}
          onClick={() => onActionClick('mark_paid')}
        />
      </div>
    </div>
  );
}

interface ActionButtonProps {
  label: string;
  icon: React.ReactNode;
  tone: 'default' | 'danger';
  eligible: boolean;
  tooltip: string;
  onClick: () => void;
}

function ActionButton({ label, icon, tone, eligible, tooltip, onClick }: ActionButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!eligible}
      title={!eligible ? tooltip : undefined}
      aria-label={label}
      style={actionButtonStyle(tone, !eligible)}
    >
      {icon}
      {label}
    </button>
  );
}

const toolbarStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: spacing.md,
  padding: `${spacing.sm}px ${spacing.md}px`,
  background: palette.blueWash,
  border: `1px solid ${palette.blue}`,
  borderRadius: radii.sm,
  marginBottom: spacing.md,
};

const selectionLabelStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: spacing.sm,
  fontSize: typography.size.caption,
  color: palette.g700,
};

const clearButtonStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderRadius: radii.sm,
  border: 'none',
  background: 'transparent',
  color: palette.blue,
  fontSize: typography.size.caption,
  fontWeight: typography.weight.semibold,
  fontFamily: 'inherit',
  cursor: 'pointer',
  textDecoration: 'underline',
};

const actionsRowStyle: React.CSSProperties = {
  display: 'flex',
  gap: spacing.sm,
  flexWrap: 'wrap',
};

const actionButtonStyle = (tone: 'default' | 'danger', disabled: boolean): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 12px',
  borderRadius: radii.sm,
  border: `1px solid ${disabled ? palette.g200 : (tone === 'danger' ? palette.red : palette.g200)}`,
  background: disabled ? palette.g100 : palette.white,
  color: disabled ? palette.g400 : (tone === 'danger' ? palette.red : palette.g700),
  fontSize: typography.size.caption,
  fontWeight: typography.weight.semibold,
  fontFamily: 'inherit',
  cursor: disabled ? 'not-allowed' : 'pointer',
  transition: `background ${transition.fast}`,
});
